import { z } from '@customerio/hydra-validation';
import type { SQLImport, SQLImportDatabase as ApiSqlImportDatabase, SQLImportRow } from '@customerio/hydra-ui-api';
import { defineEntity, readSchema } from '../shared/define-entity';

/**
 * The six SQL-import databases. These are the `?database=<type>` values the list
 * endpoint requires (it 400s without one), so the directory reads one query per
 * database and combines the results.
 */
export const SqlImportDatabase = {
  mysql: 'mysql',
  postgres: 'postgres',
  snowflake: 'snowflake',
  bigQuery: 'bigquery',
  redshift: 'redshift',
  sqlserver: 'sqlserver',
} as const;
export type SqlImportDatabase = (typeof SqlImportDatabase)[keyof typeof SqlImportDatabase];

/** All six database types, in declaration order. */
export const SQL_IMPORT_DATABASES: readonly SqlImportDatabase[] = Object.values(SqlImportDatabase);

/** The connected database an import belongs to (sideloaded via `sql_import_databases`). */
export type SqlImportDatabaseRef = Pick<ApiSqlImportDatabase, 'id' | 'type' | 'name' | 'auth_type'>;

/**
 * A SQL import with its joined database attached. The list endpoint is an
 * untyped stub in the generated client; the envelope is
 * `{ sql_imports, sql_import_databases }` and each import references its database
 * by `database_id` (there is no scalar database_type on the row), so the join is
 * resolved here from the sideload. `database` is null when the sideload does not
 * carry the referenced database.
 *
 * Fields are picked off the spec's `SQLImport` model so the names and types stay
 * tied to the API. `created_at` / `last_synced_at` (epoch seconds) are read by the
 * connections overview and ignored by the directory tab; they are optional here
 * rather than required as the model declares, because the model describes the
 * *get* reply — the list this parses is an untyped stub, and an undated sync is a
 * shape we have actually had to handle.
 */
export type SqlImport = Pick<SQLImport, 'id' | 'active' | 'database_id' | 'name'> &
  Partial<Pick<SQLImport, 'created_at' | 'last_synced_at'>> & {
    database: SqlImportDatabaseRef | null;
  };

// Keys/pk only; the list response is parsed by `parseSqlImportsResponse` below
// (a per-row schema can't express the sideload join).
const SqlImportRead = readSchema<SqlImport>(z.looseObject({ id: z.string() }));

/**
 * SQL-import rows/runs come from `GET /sql_import_rows`, not from the import
 * list (verified against `services/ui_api/sql_imports/list.go` — that response is
 * `{ sql_imports, sql_import_databases }` with no rows and a `links` URL). That
 * endpoint is typed as of services#24475, so the row shape is the generated
 * `SQLImportRow`; there is no local model to keep in step with it.
 */

/** `sql-import-row.isFailed` — a row whose status is `failed`. */
export function sqlImportRowIsFailed(row: Pick<SQLImportRow, 'status'>): boolean {
  return row.status === 'failed';
}

/** `sql-import-row.isPartiallyFailed` — failed but with some rows added/updated. */
export function sqlImportRowIsPartiallyFailed(
  row: Pick<SQLImportRow, 'status' | 'added_rows' | 'updated_rows'>,
): boolean {
  return sqlImportRowIsFailed(row) && (row.added_rows > 0 || row.updated_rows > 0);
}

/** @public — list params is the `?database=` discriminant, so each database has its own slot. */
export const sqlImport = defineEntity<SqlImport, SqlImport, SqlImportDatabase, undefined, 'workspace'>({
  name: 'sql_import',
  scope: 'workspace',
  pk: (s) => s.id,
  schema: { read: SqlImportRead },
});

export const sqlImportKeys = sqlImport.keys;

/** Per-import row-list cache slot (rows are a separate endpoint from the import list). */
export const sqlImportRowKeys = {
  list: (workspaceId: number, importId: string) => [...sqlImport.keys.all(workspaceId), 'rows', importId] as const,
};

/**
 * Parse a `{ sql_imports, sql_import_databases }` list response, resolving each
 * import's `database_id` against the sideloaded databases.
 *
 * The entries are asserted as `Partial<…>` of the spec models rather than the
 * models themselves: the list endpoint is an untyped stub, so nothing has checked
 * that the wire matches. The per-field guards below are what actually holds, and
 * they stay whether or not TypeScript still considers them narrowing.
 */
export function parseSqlImportsResponse(raw: unknown): SqlImport[] {
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  const data = raw as { sql_imports?: unknown[]; sql_import_databases?: unknown[] };

  const databasesById = new Map<string, SqlImportDatabaseRef>();
  for (const entry of data.sql_import_databases ?? []) {
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    const database = entry as Partial<ApiSqlImportDatabase>;
    if (typeof database.id === 'string') {
      databasesById.set(database.id, {
        id: database.id,
        type: String(database.type ?? ''),
        name: String(database.name ?? ''),
        auth_type: String(database.auth_type ?? ''),
      });
    }
  }

  return (data.sql_imports ?? []).map((entry) => {
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    const row = entry as Partial<SQLImport>;
    const databaseId = String(row.database_id ?? '');
    return {
      id: String(row.id ?? ''),
      active: row.active === true,
      database_id: databaseId,
      name: String(row.name ?? ''),
      database: databasesById.get(databaseId) ?? null,
      created_at: typeof row.created_at === 'number' ? row.created_at : undefined,
      last_synced_at: typeof row.last_synced_at === 'number' ? row.last_synced_at : undefined,
    };
  });
}
